import cn from 'classnames';
import Link from 'next/link';
import { Disclosure } from '@headlessui/react';
import { NavigationItem } from './navigationItems';

interface Props {
  compact?: boolean;
  navigation: NavigationItem[];
}

function Navigation({ compact, navigation }: Props) {
  return (
    <nav className="mt-5 flex-1 px-2 bg-white space-y-1">
      {navigation.map((item) =>
        !item.children ? (
          <Link
            key={item.name}
            href={item.href}
            passHref
            className={cn(
              'group flex items-center px-2 py-2 text-sm font-medium rounded-md',
              {
                'bg-gray-100 text-gray-900': item.current,
                'text-gray-600 hover:bg-gray-50 hover:text-gray-900':
                  !item.current,
              },
            )}
          >
            <item.icon
              className={cn('mr-3 flex-shrink-0 h-6 w-6', {
                'text-gray-500': item.current,
                'text-gray-400 group-hover:text-gray-500': !item.current,
              })}
              aria-hidden="true"
            />
            <span className="whitespace-nowrap">{!compact && item.name}</span>
          </Link>
        ) : (
          <Disclosure
            as="div"
            key={item.name}
            className="space-y-1"
            defaultOpen={item.current}
          >
            {({ open }) => (
              <>
                <Disclosure.Button
                  className={cn(
                    'group w-full flex items-center pl-2 pr-1 py-2 text-left text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-rum-500',
                    {
                      'bg-gray-100 text-gray-900': item.current,
                      'bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900':
                        !item.current,
                    },
                  )}
                >
                  <item.icon
                    className="mr-3 flex-shrink-0 h-6 w-6 text-gray-400 group-hover:text-gray-500"
                    aria-hidden="true"
                  />
                  <span className="flex-1 whitespace-nowrap">
                    {!compact && item.name}
                  </span>
                  <svg
                    className={cn(
                      'ml-3 flex-shrink-0 h-5 w-5 transform group-hover:text-gray-400 transition-colors ease-in-out duration-150',
                      {
                        'text-gray-400 rotate-90': open,
                        'text-gray-300': !open,
                      },
                    )}
                    viewBox="0 0 20 20"
                    aria-hidden="true"
                  >
                    <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
                  </svg>
                </Disclosure.Button>
                <Disclosure.Panel className="space-y-1">
                  {!compact &&
                    item.children.map((subItem) => (
                      <Disclosure.Button
                        key={subItem.name}
                        as={Link}
                        href={subItem.href}
                        passHref
                      >
                        <span
                          className={cn(
                            'group w-full flex items-center pl-11 pr-2 py-2 text-sm  rounded-md',
                            {
                              'text-gray-900 font-semibold': subItem.current,
                              'font-medium bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900':
                                !subItem.current,
                            },
                          )}
                        >
                          {subItem.icon && (
                            <subItem.icon
                              className={cn('-ml-6 mr-2 h-4 w-4', {
                                'text-gray-900': item.current,
                                'text-gray-600 group-hover:text-gray-900':
                                  !item.current,
                              })}
                              aria-hidden="true"
                            />
                          )}
                          {subItem.name}
                        </span>
                      </Disclosure.Button>
                    ))}
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        ),
      )}
    </nav>
  );
}

export default Navigation;
